import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Label } from 'components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';
import ErrorText from '../ErrorText';
import Text from '../Text';
import { date_picker_icon } from 'asset/icons';
import { ReactSVG } from 'react-svg';

interface DatePickerProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  error?: string;
}

const DatePicker = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  required = false,
  disabled = false,
  placeholder = 'Select date',
  className,
  error,
}: DatePickerProps) => {
  const [open, setOpen] = useState<boolean>(false);

  // Disable dates outside range
  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  return (
    <div className={cn('w-full gap-2 ', className)}>
      {label && (
        <Label className='flex items-center gap-1 px-1'>
          <Text text={label} className='text-muted-foreground' />
          {required && <span className='text-red-500'>*</span>}
        </Label>
      )}
      <div className='relative mt-2'>
        <Popover
          open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant='outline' id='date' className={cn(
              'w-full rounded-full bg-input border-0 py-2.5 md:py-3 transition-colors duration-150',
              'pr-12',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
              disabled={disabled}>
              <Text className={`flex-1 text-left ${value ? "text-black" : " text-gray-500"}`} text={value ? value.toLocaleDateString() : placeholder} />
              <ReactSVG src={date_picker_icon} />
            </Button>
          </PopoverTrigger>

          <PopoverContent className='w-auto overflow-hidden p-0' align='start'>
            <Calendar
              mode='single'
              selected={value ?? undefined}
              captionLayout='dropdown'
              disabled={isDateDisabled}
              onSelect={(selectedDate) => {
                if (!selectedDate) return;
                onChange(selectedDate);
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>

      {error && <ErrorText text={error} />}
    </div>
  );
};

export default DatePicker;
